import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import Select from "react-select";
import { useSelector } from "react-redux";
import { selectUser } from "../../features/userSlice";
import { useNavigate } from "react-router-dom";
import {
  FaBalanceScale,
  FaTruck,
  FaClipboardList,
  FaCheckCircle,
  FaSyncAlt,
  FaArrowUp,
  FaArrowDown,
  FaHome,
  FaTimesCircle,
} from "react-icons/fa";
import styles from "./ReWeighVehicle.module.css";
import FloatingActions from "../../components/FloatingActions";

const wbOptions = Array.from({ length: 12 }, (_, i) => {
  const n = String(i + 1);
  return `${n}`;
});

const getPNumber = (row) =>
  row?.P_NUMBER ?? row?.p_number ?? row?.P_Number ?? "";

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: "44px",
    borderRadius: "12px",
    borderColor: state.isFocused ? "#38bdf8" : "#d8e4f0",
    boxShadow: state.isFocused
      ? "0 0 0 4px rgba(14,165,233,0.14)"
      : "none",
    fontSize: "0.96rem",
  }),
  menu: (base) => ({
    ...base,
    zIndex: 9999,
    borderRadius: "12px",
    overflow: "hidden",
  }),
  option: (base, state) => ({
    ...base,
    fontSize: "0.92rem",
    fontWeight: state.isSelected ? 800 : 500,
  }),
};

const ReWeighVehicle = () => {
  const user = useSelector(selectUser);
  const token = user?.token;
  const emp_id = user?.emp_id;
  const userCode = user?.emp_id || user?.id || user?.user_code || "SYSTEM";

  const navigate = useNavigate();

  const topRef = useRef(null);
  const bottomRef = useRef(null);

  const [wbNo, setWbNo] = useState("");
  const [gateEntries, setGateEntries] = useState([]);
  const [selectedPNumber, setSelectedPNumber] = useState("");
  const [detail, setDetail] = useState(null);

  const [loadingList, setLoadingList] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [saving, setSaving] = useState(false);

  const [toast, setToast] = useState("");
  const [popup, setPopup] = useState({ open: false, title: "", message: "" });

  const showPopup = (message, title = "Notice") =>
    setPopup({ open: true, title, message });

  const closePopup = () => setPopup({ open: false, title: "", message: "" });

  const selectedRow = useMemo(() => {
    return gateEntries.find(
      (r) => String(getPNumber(r)) === String(selectedPNumber)
    );
  }, [gateEntries, selectedPNumber]);

  const gateEntryOptions = useMemo(() => {
    return gateEntries
      .filter((r) => getPNumber(r))
      .map((r) => ({
        value: String(getPNumber(r)),
        label: `${r.P_DOCNUMBER || "-"} — ${r.P_TRUCK || "-"} — Status ${
          r.P_STATUS ?? "-"
        }`,
        row: r,
      }));
  }, [gateEntries]);

  const selectedOption = useMemo(() => {
    if (!selectedRow) return null;

    return {
      value: String(getPNumber(selectedRow)),
      label: `${selectedRow.P_DOCNUMBER || "-"} — ${
        selectedRow.P_TRUCK || "-"
      } — Status ${selectedRow.P_STATUS ?? "-"}`,
      row: selectedRow,
    };
  }, [selectedRow]);

  const statusText = useMemo(() => getStatusText(detail?.P_STATUS), [detail]);

  const nextStatus = useMemo(() => {
    if (!detail) return "";
    if (Number(detail.P_STATUS) === 4) return 3;
    if (Number(detail.P_STATUS) === 3) return 1;
    return "";
  }, [detail]);

  const nextStatusText = useMemo(() => getStatusText(nextStatus), [nextStatus]);

  const fetchGateEntries = async () => {
    if (!wbNo) {
      showPopup("Please select WB first.", "Validation");
      return;
    }

    try {
      setLoadingList(true);
      setGateEntries([]);
      setSelectedPNumber("");
      setDetail(null);

      const res = await axios.post("/api/wbauto/reweigh/gateEntries", {
        emp_id,
        token,
        wbNo,
      });

      const rows = Array.isArray(res.data) ? res.data : [];
      console.log("Reweigh gateEntries rows:", rows);

      setGateEntries(rows);

      if (rows.length === 0) {
        setToast("No eligible vehicles found for selected WB.");
      } else {
        const missingPNumber = rows.some((r) => !getPNumber(r));
        if (missingPNumber) {
          showPopup(
            "Vehicle list loaded, but P_NUMBER is missing in API response. Please add P_NUMBER in getReweighGateEntries SELECT.",
            "API Data Issue"
          );
        }
      }
    } catch (err) {
      console.error("reweigh gateEntries error:", err);
      showPopup(
        err?.response?.data?.message || "Failed to load gate entries.",
        "Error"
      );
    } finally {
      setLoadingList(false);
    }
  };

  const fetchDetail = async (pNumber) => {
    console.log("fetchDetail pNumber:", pNumber);

    if (!pNumber) {
      setDetail(null);
      return;
    }

    try {
      setLoadingDetail(true);
      setDetail(null);

      const payload = {
        emp_id,
        token,
        pNumber: Number(pNumber),
      };

      console.log("Detail API payload:", payload);

      const res = await axios.post("/api/wbauto/reweigh/detail", payload);

      console.log("Detail API response:", res.data);
      setDetail(res.data || null);
    } catch (err) {
      console.error("reweigh detail error:", err);
      console.error("detail response:", err?.response?.data);

      showPopup(
        err?.response?.data?.message || "Failed to load vehicle details.",
        "Error"
      );
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleAcceptReweigh = async () => {
    if (!wbNo) {
      showPopup("Please select WB first.", "Validation");
      return;
    }

    if (!selectedPNumber) {
      showPopup("Please select Gate Entry Number.", "Validation");
      return;
    }

    if (!detail) {
      showPopup("Vehicle details not loaded.", "Validation");
      return;
    }

    if (![3, 4].includes(Number(detail.P_STATUS))) {
      showPopup("Only status 3 or 4 can be accepted for re-weigh.", "Validation");
      return;
    }

    const ok = window.confirm(
      `Accept re-weigh for ${detail.P_DOCNUMBER} / ${detail.P_TRUCK}?\n\nStatus will change from ${detail.P_STATUS} to ${nextStatus}.`
    );

    if (!ok) return;

    try {
      setSaving(true);

      const payload = {
        emp_id,
        token,
        pNumber: Number(selectedPNumber),
        wbNo,
        userCode,
      };

      console.log("Accept Reweigh payload:", payload);

      const res = await axios.post("/api/wbauto/reweigh/accept", payload);

      if (res.data?.success) {
        setToast("✅ Vehicle accepted for re-weigh successfully.");
        await fetchDetail(selectedPNumber);
        topRef.current?.scrollIntoView({ behavior: "smooth" });
      } else {
        showPopup(res.data?.message || "Failed to accept re-weigh.", "Error");
      }
    } catch (err) {
      console.error("accept reweigh error:", err);
      showPopup(
        err?.response?.data?.message || "Error while accepting re-weigh.",
        "Error"
      );
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(""), 2600);
    return () => clearTimeout(t);
  }, [toast]);

  useEffect(() => {
    if (wbNo) {
      fetchGateEntries();
    } else {
      setGateEntries([]);
      setSelectedPNumber("");
      setDetail(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wbNo]);

  if (!token) {
    return (
      <div className={styles.page}>
        <div className={styles.loginBox}>
          <FaBalanceScale className={styles.loginIcon} />
          <p>You must be logged in to use Re-Weigh Vehicle.</p>
          <button className={styles.primaryBtn} onClick={() => navigate("/")}>
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div ref={topRef} />

      {toast && <div className={styles.toast}>{toast}</div>}

      {popup.open && (
        <div className={styles.modalBackdrop} onClick={closePopup}>
          <div className={styles.modalSheet} onClick={(e) => e.stopPropagation()}>
            <h4>{popup.title}</h4>
            <p>{popup.message}</p>
            <button className={styles.primaryBtn} onClick={closePopup}>
              OK
            </button>
          </div>
        </div>
      )}

      <div className={styles.hero}>
        <div className={styles.heroIcon}>
          <FaBalanceScale />
        </div>
        <div>
          <h2>Re-Weigh Vehicle</h2>
          <p>Accept vehicle for re-weigh and rollback WB status safely.</p>
        </div>
      </div>

      <div className={styles.panel}>
        <div className={styles.sectionTitle}>
          <FaClipboardList />
          <span>Selection</span>
        </div>

        <div className={styles.formGrid}>
          <div className={styles.formItem}>
            <label>Select WB</label>
            <select
              className={styles.input}
              value={wbNo}
              onChange={(e) => setWbNo(e.target.value)}
            >
              <option value="">-- Select WB --</option>
              {wbOptions.map((w) => (
                <option key={w} value={w}>
                  WB {w}
                </option>
              ))}
            </select>
          </div>

          <div className={styles.formItem}>
            <label>Gate Entry Number / Vehicle</label>

            <Select
              styles={selectStyles}
              isClearable
              isSearchable
              isDisabled={!wbNo || loadingList}
              placeholder={
                loadingList
                  ? "Loading..."
                  : "Type Gate Entry No or Vehicle No..."
              }
              value={selectedOption}
              options={gateEntryOptions}
              filterOption={(option, inputValue) => {
                const q = String(inputValue || "").toUpperCase();
                const r = option.data.row;

                return (
                  String(r.P_DOCNUMBER || "").toUpperCase().includes(q) ||
                  String(r.P_TRUCK || "").toUpperCase().includes(q)
                );
              }}
              onChange={(opt) => {
                const pNumber = opt?.value || "";
                console.log("Selected P_NUMBER:", pNumber);

                setSelectedPNumber(pNumber);

                if (pNumber) {
                  fetchDetail(pNumber);
                } else {
                  setDetail(null);
                }
              }}
              noOptionsMessage={() => "No matching Gate Entry / Vehicle found"}
            />
          </div>
        </div>

        <button
          className={styles.refreshBtn}
          onClick={fetchGateEntries}
          disabled={!wbNo || loadingList}
        >
          <FaSyncAlt />
          {loadingList ? "Refreshing..." : "Refresh Vehicle List"}
        </button>

        <div className={styles.resultMeta}>
          {loadingList
            ? "Loading vehicles..."
            : `${gateEntries.length} eligible vehicle${
                gateEntries.length === 1 ? "" : "s"
              } found`}
        </div>
      </div>

      {loadingDetail && (
        <div className={styles.loadingBox}>Loading vehicle details...</div>
      )}

      {detail && (
        <>
          <div className={styles.summaryCard}>
            <div className={styles.truckBadge}>
              <FaTruck />
            </div>

            <div className={styles.summaryMain}>
              <h3>{detail.P_TRUCK || "-"}</h3>
              <p>{detail.P_DOCNUMBER || "-"}</p>
              <small>P_NUMBER: {detail.P_NUMBER || selectedPNumber}</small>
            </div>

            <div
              className={`${styles.statusPill} ${
                styles[`status${detail.P_STATUS}`] || ""
              }`}
            >
              Status {detail.P_STATUS}
            </div>
          </div>

          <div className={styles.panel}>
            <div className={styles.sectionTitle}>
              <FaBalanceScale />
              <span>Existing Details</span>
            </div>

            <div className={styles.infoGrid}>
              <Info label="Vehicle Number" value={detail.P_TRUCK} highlight />
              <Info label="Gate Entry No" value={detail.P_DOCNUMBER} highlight />
              <Info label="Current Status" value={statusText} />
              <Info label="Selected WB" value={`WB ${wbNo}`} />
              <Info label="Item" value={detail.P_Item_name} />
              <Info label="Party" value={detail.P_Party_name} />
              <Info label="Transporter" value={detail.p_Trans_name} />
              <Info label="Mines Code" value={detail.P_Mines_Code} />
              <Info label="Load From" value={detail.p_load_from} />
              <Info label="Load To" value={detail.p_load_to} />
            </div>

            <div className={styles.weightCards}>
              <WeightCard
                title="1st Weighment"
                wb={detail.P_FIRSTWBNO}
                date={detail.P_FIRSTDATE}
                weight={detail.P_FIRSTWT}
              />

              <WeightCard
                title="2nd Weighment"
                wb={detail.P_SECONDWBNO}
                date={detail.P_SECONDDATE}
                weight={detail.P_SECONDWT}
              />
            </div>

            <div className={styles.netWeightBox}>
              <span>Net Weight</span>
              <strong>{formatWeight(detail.P_NETWT)} MT</strong>
            </div>
          </div>

          <div className={styles.panel}>
            <div className={styles.sectionTitle}>
              <FaSyncAlt />
              <span>Previous Re-Weigh Info</span>
            </div>

            <div className={styles.infoGrid}>
              <Info label="Re-Weigh Flag" value={detail.P_ReWeighFlag} />
              <Info label="Recheck WB" value={detail.P_RechekWB} />
              <Info label="Recheck Date" value={formatDate(detail.P_RechekDate)} />
              <Info label="Recheck By" value={detail.P_RechekBy} />
            </div>
          </div>

          <div className={styles.actionPanel}>
            <div>
              <div className={styles.actionTitle}>Accept Re-Weight</div>

              <div className={styles.actionSub}>
                Status will change from{" "}
                <strong>{detail.P_STATUS}</strong> to{" "}
                <strong>{nextStatus}</strong>
              </div>

              <div className={styles.actionSubSmall}>
                {statusText} → {nextStatusText}
              </div>

              {selectedRow && (
                <div className={styles.actionSubSmall}>
                  Updating by P_NUMBER:{" "}
                  <strong>{getPNumber(selectedRow)}</strong>
                </div>
              )}
            </div>
            <div className={styles.actionButtons}>
                <button
                    className={styles.acceptBtn}
                    onClick={handleAcceptReweigh}
                    disabled={saving || ![3, 4].includes(Number(detail.P_STATUS))}
                >
                    <FaCheckCircle />
                    {saving ? "Updating..." : "Accept Re-Weight"}
                </button>

                <button
                    className={styles.cancelBtn}
                    type="button"
                    onClick={() => navigate("/dashboard")}
                >
                    <FaTimesCircle />
                    Cancel
                </button>
            </div>
          </div>
        </>
      )}

      {!detail && !loadingDetail && (
        <div className={styles.emptyBox}>
          Select WB and Gate Entry Number to view vehicle details.
        </div>
      )}

      <FloatingActions topRef={topRef} bottomRef={bottomRef} />
      <div ref={bottomRef} />
    </div>
  );
};

const Info = ({ label, value, highlight }) => (
  <div className={`${styles.infoItem} ${highlight ? styles.highlightInfo : ""}`}>
    <span>{label}</span>
    <strong>{value || "-"}</strong>
  </div>
);

const WeightCard = ({ title, wb, date, weight }) => (
  <div className={styles.weightCard}>
    <div className={styles.weightTitle}>{title}</div>

    <div className={styles.weightRow}>
      <span>WB No</span>
      <strong>{wb || "-"}</strong>
    </div>

    <div className={styles.weightRow}>
      <span>Date</span>
      <strong>{formatDate(date)}</strong>
    </div>

    <div className={styles.weightValue}>{formatWeight(weight)} MT</div>
  </div>
);

function getStatusText(status) {
  const s = Number(status);
  if (s === 1) return "Gate Entry / Waiting for 1st Weight";
  if (s === 3) return "1st Weight Completed";
  if (s === 4) return "2nd Weight Completed";
  return status ? `Status ${status}` : "-";
}

function formatDate(v) {
  if (!v) return "-";
  const d = new Date(v);
  if (isNaN(d)) return "-";

  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatWeight(v) {
  if (v === null || v === undefined || v === "") return "0.000";
  const n = Number(v);
  if (isNaN(n)) return v;
  return n.toFixed(3);
}

export default ReWeighVehicle;